// pages/EnvironmentalParameters.jsx

import { useState, useEffect } from "react";
import apiClient from "../../../shared/API/apiClient";
import "../../../styles/pages/environmentalparameters.css";
import ParameterModal from "./ParameterModal";
import Toast from "../../../shared/components/Toast";
import { Lock } from "lucide-react";
import { Power } from "lucide-react";

const EnvironmentalParameters = () => {
  const [systemParams, setSystemParams] = useState([]);
  const [customParams, setCustomParams] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const [toast, setToast] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchParameters = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get("/api/parameters");

        if (res.data.code === 200) {
          const data = res.data;

          const systemData = (data.system_parameters || []).map((item) => ({
            id: item.id,
            name: item.name,
            code: item.code,
            unit: item.unit,
            min: item.min_limit,
            max: item.max_limit,
            status: item.status === "active" ? "Active" : "Inactive",
          }));

          const customData = (data.custom_parameters || []).map((item) => ({
            id: item.id,
            name: item.name,
            code: item.code,
             unit_id: item.unit_id, 


            unit: item.unit,
            min: item.min_limit,
            max: item.max_limit,
            status: item.status === "active" ? "Active" : "Inactive",
          }));

          setSystemParams(systemData);
          setCustomParams(customData);
        }
      } catch (err) {
        console.error("Failed to fetch parameters:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchParameters();
  }, []);

  const openAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditData(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditData(null);
  };


 const handleSaved = (data, isEdit) => {
  if (isEdit) {
    setCustomParams((prev) =>
      prev.map((p) => (p.id === data.id ? data : p))
    );
    setToast({ message: "Parameter updated successfully", type: "success" });
  } else {
    setCustomParams((prev) => [...prev, data]);
    setToast({ message: "Parameter added successfully", type: "success" });
  }

  closeModal();
};


  //Handle toggler Status change API

  const handleToggleStatus = async (item) => {
    try {
      const res = await apiClient.post(
        `/api/parameters/toggle-status/${item.id}`
      );

      if (res.data.code === 200) {
        const newStatus = res.data.status === "active" ? "Active" : "Inactive";

        // state update
        setCustomParams((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: newStatus } : p))
        );

        setToast({
          message:
            res.data.message === "parameter_activated_successfully"
              ? "Parameter activated successfully"
              : "Parameter deactivated successfully",
          type: "success",
        });
      }
    } catch (err) {
      console.error("Toggle status failed:", err);
      setToast({
        message: "Something went wrong",
        type: "error",
      });
    }
  };


  return (
    <div className="dash-page">
      <div className="le-page-header">
        <div>
          <div className="dash-page-subtitle">
            Configuration / Environmental Parameters
          </div>
          <div className="dash-page-title">Environmental Parameters</div>
          <div className="dash-page-subtitle">
            Manage system-defined and custom environmental monitoring
            parameters.
          </div>
        </div>
        <br></br>
        <button className="org-btn-save le-add-btn" onClick={openAdd}>
          + &nbsp;Add Parameters
        </button>
      </div>

      {/* System Parameters */}
      <div className="ep-card">
        <div className="ep-card-header">
          <span className="ep-lock-icon">
            <Lock size={16} className="ep-lock-icon" />
          </span>
          <span className="ep-card-title">System Parameters</span>
          <span className="ep-badge">Read Only</span>
        </div>

        <p className="ep-card-desc">
          Standard parameters defined by the system. These cannot be edited or
          removed.
        </p>

        {loading ? (
          <div className="table-loader-wrap-full">
            <div className="table-loader-wrap-full">
              <div className="table-loader"></div>
              <p className="table-loader-text">System Parameters loading...</p>
            </div>
          </div>
        ) : (
          <table className="le-table">
            <thead>
              <tr>
                <th>Parameter Name</th>
                <th>Code</th>
                <th>Unit</th>
                <th>Min Limit</th>
                <th>Max Limit</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {systemParams.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    No data available
                  </td>
                </tr>
              ) : (
                systemParams.map((item, i) => (
                  <tr key={i}>
                    <td className="le-entity-name">{item.name}</td>
                    <td>{item.code}</td>
                    <td>{item.unit}</td>
                    <td>{item.min}</td>
                    <td>{item.max}</td>
                    <td>
                      <span className="ep-status">{item.status}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Custom Parameters */}
      <div className="ep-card">
        <div className="ep-card-header">
          <span className="ep-card-title">Your Custom Parameters</span>
        </div>

        <p className="ep-card-desc">
          Parameters added by your organization. You can edit or deactivate them
          at any time.
        </p>

        {loading ? (
          <div className="table-loader-wrap-full">
            <div className="table-loader-wrap-full">
              <div className="table-loader"></div>
              <p className="table-loader-text">Custom Parameters loading...</p>
            </div>
          </div>
        ) : (
          <table className="le-table">
            <thead>
              <tr>
                <th>Parameter Name</th>
                <th>Code</th>
                <th>Unit</th>
                <th>Min Limit</th>
                <th>Max Limit</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customParams.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    No data available
                  </td>
                </tr>
              ) : (
                customParams.map((item) => (
                  <tr key={item.id}>
                    <td className="le-entity-name">{item.name}</td>
                    <td>{item.code}</td>
                    <td>{item.unit}</td>
                    <td>{item.min}</td>
                    <td>{item.max}</td>
                    <td>
                      <span className="ep-status">{item.status}</span>
                    </td>
                    <td>
                      <div className="le-actions">
                        <span
                          className="action-item edit"
                          onClick={() => openEdit(item)}
                        >
                          <img
                            src="https://cdn-icons-png.flaticon.com/512/1828/1828911.png"
                            alt="edit"
                            className="action-icon"
                          />
                          Edit
                        </span>

                        <span
                          className={`action-item ${
                            item.status === "Active" ? "deactivate" : "activate"
                          }`}
                          onClick={() => handleToggleStatus(item)}
                        >
                          <Power size={16} />
                          {item.status === "Active" ? "Deactivate" : "Activate"}

                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <ParameterModal
          onClose={closeModal}
          onSaved={handleSaved}
          editData={editData}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default EnvironmentalParameters;
